import { useEffect } from 'react';

import type { Post } from '~/interfaces/post';
import { getUrlParamValue } from '~/utils/getUrlParamValue';
import { isValidUrl } from '~/utils/isValidUrl';

const useClickTracking = ({
  post,
  visitToken,
}: {
  post: Post;
  visitToken: string;
}) => {
  // setup click tracking for links
  useEffect(() => {
    function trackClick(e: MouseEvent) {
      try {
        let target = e.target as HTMLElement;
        while (
          target &&
          target.id !== 'content-blocks' && // bail if we reach the content-blocks
          target.tagName !== 'BODY' // bail if we reach the body
        ) {
          if (target.tagName === 'A') {
            const url = (target as HTMLAnchorElement).href;
            if (!isValidUrl(url)) {
              break;
            }
            const linkId = getUrlParamValue(url, '_bhlid');
            const payload = {
              url,
              post_id: post.id,
              publication_id: post.publication_id,
              visit_token: visitToken,
              ...(linkId ? { link_id: linkId } : {}),
            };
            // send click-event to server then process the click event
            fetch('/clicks', {
              method: 'post',
              body: JSON.stringify(payload),
              headers: {
                'content-type': 'application/json',
              },
            })
              .then(/* do nothing */)
              .catch(/* do nothing */);

            break;
          }
          target = target.parentElement as HTMLElement;
        }
      } catch {
        // do nothing
      }
    }

    // add listener to track clicks (remove existing)
    document.removeEventListener('click', trackClick);
    document.addEventListener('click', trackClick);

    // remove listener on cleanup
    return () => {
      document.removeEventListener('click', trackClick);
    };
  }, [post.id, post.publication_id, visitToken]);
};

export default useClickTracking;
